import {
  Box,
  Chip,
  InputAdornment,
  TextField,
  Select,
  MenuItem,
  FormControl,
  Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import TodayOutlinedIcon from "@mui/icons-material/TodayOutlined";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs, { Dayjs } from "dayjs";
import { colorBorder, colorSurface, colorTextMuted, radiusInput } from "src/design/tokens";
import { useTranslation } from "react-i18next";
import { Card } from "src/design";

export type DateFilter = "this_week" | "today" | "upcoming" | "custom";
export type StatusFilter =
  | "all"
  | "CONFIRMED"
  | "PENDING"
  | "CANCELED"
  | "CHECKED_IN"
  | "NO_SHOW";

interface Props {
  search: string;
  onSearchChange: (v: string) => void;
  dateFilter: DateFilter;
  onDateFilterChange: (v: DateFilter) => void;
  customStart: Dayjs;
  customEnd: Dayjs;
  onCustomRangeChange: (start: Dayjs, end: Dayjs) => void;
  statusFilter: StatusFilter;
  onStatusFilterChange: (v: StatusFilter) => void;
  /**
   * The restaurant only bought today's service: the date filters are replaced
   * by the day they would all resolve to, rather than being offered and then
   * refused. The status filter and the search stay — they narrow within the
   * day, which is exactly what was paid for.
   */
  sameDayOnly?: boolean;
}

export const BookingSearchBar = ({
  search,
  onSearchChange,
  dateFilter,
  onDateFilterChange,
  customStart,
  customEnd,
  onCustomRangeChange,
  statusFilter,
  onStatusFilterChange,
  sameDayOnly = false,
}: Props) => {
  const { t } = useTranslation();

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box sx={{ mb: 2.5 }}>
        <Card>
          <TextField
            size="small"
            placeholder={t("bookings.searchPlaceholder")}
            value={search}
            onChange={(e) => onSearchChange(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ fontSize: 18, color: colorTextMuted }} />
                </InputAdornment>
              ),
            }}
            sx={{
              flex: 1,
              minWidth: 200,
              "& .MuiOutlinedInput-root": {
                borderRadius: radiusInput,
                backgroundColor: colorSurface,
              },
            }}
          />

          {sameDayOnly ? (
            <Chip
              icon={<TodayOutlinedIcon sx={{ fontSize: 16 }} />}
              label={`${t("bookings.filters.today")} · ${dayjs().format(
                "D MMM YYYY",
              )}`}
              variant="outlined"
              sx={{ borderRadius: radiusInput, backgroundColor: colorSurface }}
            />
          ) : (
            <FormControl size="small" sx={{ minWidth: 140 }}>
              <Select
                value={dateFilter}
                onChange={(e) =>
                  onDateFilterChange(e.target.value as DateFilter)
                }
                sx={{
                  borderRadius: radiusInput,
                  backgroundColor: colorSurface,
                  border: `1px solid ${colorBorder}`,
                }}
              >
                <MenuItem value="this_week">{t("bookings.filters.thisWeek")}</MenuItem>
                <MenuItem value="today">{t("bookings.filters.today")}</MenuItem>
                <MenuItem value="upcoming">{t("bookings.filters.upcoming")}</MenuItem>
                <MenuItem value="custom">{t("bookings.filters.customRange")}</MenuItem>
              </Select>
            </FormControl>
          )}

          <FormControl size="small" sx={{ minWidth: 145 }}>
            <Select
              value={statusFilter}
              onChange={(e) => onStatusFilterChange(e.target.value as StatusFilter)}
              sx={{
                borderRadius: radiusInput,
                backgroundColor: colorSurface,
                border: `1px solid ${colorBorder}`,
              }}
            >
              <MenuItem value="all">{t("bookings.filters.allStatuses")}</MenuItem>
              <MenuItem value="CONFIRMED">{t("bookings.status.confirmed")}</MenuItem>
              <MenuItem value="PENDING">{t("bookings.status.pending")}</MenuItem>
              <MenuItem value="CHECKED_IN">{t("bookings.status.checkedIn")}</MenuItem>
              <MenuItem value="NO_SHOW">{t("bookings.status.noShow")}</MenuItem>
              <MenuItem value="CANCELED">{t("bookings.status.cancelled")}</MenuItem>
            </Select>
          </FormControl>
        </Card>

        {sameDayOnly && (
          <Typography
            variant="caption"
            sx={{ display: "block", color: colorTextMuted, mt: 1 }}
          >
            {t("bookings.filters.sameDayOnly")}
          </Typography>
        )}

        {!sameDayOnly && dateFilter === "custom" && (
          <Box sx={{ display: "flex", gap: 1.5, mt: 1.5, flexWrap: "wrap" }}>
            <DatePicker
              label={t("bookings.filters.from")}
              value={customStart}
              onChange={(v) => v && onCustomRangeChange(v, customEnd)}
              maxDate={customEnd}
              renderInput={(params) => (
                <TextField
                  {...params}
                  size="small"
                  sx={{
                    flex: 1,
                    minWidth: 160,
                    "& .MuiOutlinedInput-root": {
                      borderRadius: radiusInput,
                      backgroundColor: colorSurface,
                    },
                  }}
                />
              )}
            />
            <DatePicker
              label={t("bookings.filters.to")}
              value={customEnd}
              onChange={(v) => v && onCustomRangeChange(customStart, v)}
              minDate={customStart}
              renderInput={(params) => (
                <TextField
                  {...params}
                  size="small"
                  sx={{
                    flex: 1,
                    minWidth: 160,
                    "& .MuiOutlinedInput-root": {
                      borderRadius: radiusInput,
                      backgroundColor: colorSurface,
                    },
                  }}
                />
              )}
            />
          </Box>
        )}
      </Box>
    </LocalizationProvider>
  );
};
